import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams, useParams, useNavigate } from 'react-router-dom';
import { api, useAuth, useLanguage } from '@/lib/context';
import { useTranslatedContent } from '@/hooks/useContentTranslation';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { 
  Wrench, Search, MapPin, Euro, Calendar, Package, 
  ChevronRight, Filter, Plus, Eye, Clock, Check, X,
  Truck, Shield, Star, ArrowLeft, Languages
} from 'lucide-react';
import { toast } from 'sonner';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { ShareButton, ShareButtonsInline } from '@/components/ShareButton';
import { SEOHead } from '@/components/SEOHead';
import { CategoryCombobox } from '@/components/CategoryCombobox';

// Shared Navigation
const RentalsNav = () => {
  const { t } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <nav className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="text-2xl font-bold text-brand">KEIKKA</Link>
          
          <div className="hidden md:flex items-center gap-6">
            <Link to="/jobs" className="text-muted-foreground hover:text-foreground transition">
              {t('nav.jobs')}
            </Link>
            <Link to="/rentals" className="text-foreground font-medium">
              {t('nav.rentals')}
            </Link>
            <Link to="/auto" className="text-muted-foreground hover:text-foreground transition">
              {t('nav.autoServices')}
            </Link>
          </div>

          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            {user ? (
              <>
                <Link to="/dashboard">
                  <Button variant="outline" size="sm">{t('nav.dashboard')}</Button>
                </Link>
                <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/'); }}>
                  {t('auth.logout')}
                </Button>
              </>
            ) : (
              <Link to="/login">
                <Button size="sm">{t('auth.login')}</Button>
              </Link>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

// Category icons
const categoryIcons = {
  power_tools: '⚡',
  hand_tools: '🔧',
  construction: '🏗️',
  garden: '🌱',
  cleaning: '🧹',
  vehicles: '🚗',
  lifting: '🏋️',
  safety: '🦺',
  electrical: '🔌',
  plumbing: '🚿',
  welding: '🔥',
  painting: '🎨',
  other: '📦'
};

// Equipment Card Component - with defensive null checks
// Optional dateFrom/dateTo props let the badge + booking link reflect the
// user's chosen rental window.
const EquipmentCard = ({ item, dateFrom, dateTo }) => {
  const { t } = useLanguage();
  
  // Safe property access with fallbacks - call unconditionally for hooks
  const title = item?.title || '';
  const description = item?.description || '';
  
  // Dynamic translation for user-generated content - must be called before any conditional return
  const { text: translatedTitle, isTranslated: titleTranslated } = useTranslatedContent(title);
  const { text: translatedDesc, isTranslated: descTranslated } = useTranslatedContent(description);
  const isContentTranslated = titleTranslated || descTranslated;
  
  // Defensive: return null if item is missing or invalid
  if (!item || typeof item !== 'object' || !item.id) {
    return null;
  }
  
  // Safe property access with fallbacks using text keys
  const displayTitle = translatedTitle || t('common.untitled');
  const displayDescription = translatedDesc || t('common.noDescription');
  const category = item.category;
  const status = item.status;
  const city = item.city;
  const country = item.country;
  const translatedCountry = t(`location.countries.${country}`);
  const countryDisplay = translatedCountry !== `location.countries.${country}` ? translatedCountry : country;
  const location = [city, countryDisplay].filter(Boolean).join(', ') || t('common.locationNotSpecified');
  const pricePerDay = item.price_per_day;
  const pricePerWeek = item.price_per_week;
  const brand = item.brand;
  const model = item.model;
  const deliveryAvailable = item.delivery_available;
  const depositAmount = item.deposit_amount;
  
  return (
    <Card className="card-hover h-full flex flex-col" data-testid={`equipment-card-${item.id}`}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="text-2xl">{categoryIcons[category]}</div>
          <AvailabilityBadge {...computeAvailability(item, dateFrom, dateTo)} t={t} />
        </div>
        {/* Brand + model headline (Hilti/Finn.no style) — when both exist
            we lift them into the title row for clearer at-a-glance scanning. */}
        {brand && (
          <div className="text-[11px] font-bold uppercase tracking-wide text-orange-600" data-testid={`equipment-brand-${item.id}`}>
            {brand}{model ? ` · ${model}` : ''}
          </div>
        )}
        <CardTitle className="text-lg line-clamp-2">
          {displayTitle}
          {isContentTranslated && (
            <Languages className="inline-block ml-2 h-3.5 w-3.5 text-blue-500" title={t('common.translated')} />
          )}
        </CardTitle>
        <CardDescription className="line-clamp-2">{displayDescription}</CardDescription>
      </CardHeader>

      <CardContent className="flex-1 space-y-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <MapPin className="h-4 w-4" />
          <span>{location}</span>
        </div>

        {pricePerDay != null && (
          <div className="flex items-baseline gap-2 text-sm">
            <span className="text-lg font-bold text-slate-900">€{pricePerDay}</span>
            <span className="text-xs text-muted-foreground">/{t('common.days').slice(0, -1)}</span>
            {pricePerWeek != null && (
              <span className="text-xs text-muted-foreground ml-1">
                · €{pricePerWeek}/{t('rentals.pricePerWeek').toLowerCase()}
              </span>
            )}
          </div>
        )}

        <div className="flex gap-2 flex-wrap">
          {deliveryAvailable && (
            <Badge variant="outline" className="text-xs">
              <Truck className="h-3 w-3 mr-1" /> {t('rentals.delivery')}
            </Badge>
          )}
          {depositAmount != null && (
            <Badge variant="outline" className="text-xs">
              <Shield className="h-3 w-3 mr-1" /> €{depositAmount} {t('rentals.deposit').toLowerCase()}
            </Badge>
          )}
          {Array.isArray(item.specs) && item.specs.slice(0, 3).map((s, i) => (
            s.value ? (
              <Badge
                key={i}
                variant="outline"
                className="text-xs bg-slate-50 border-slate-200 text-slate-700"
                data-testid={`spec-badge-${item.id}-${i}`}
              >
                {s.value}{s.unit ? ` ${s.unit}` : ''}
              </Badge>
            ) : null
          ))}
        </div>
      </CardContent>
      
      <CardFooter className="flex justify-between items-center gap-2">
        <ShareButtonsInline 
          entityType="rental" 
          entityId={item.id} 
          title={title}
        />
        <div className="flex gap-2">
          <Link to={`/rentals/${item.id}`}>
            <Button variant="outline" size="sm" data-testid={`view-equipment-${item.id}`}>
              {t('common.viewDetails')}
            </Button>
          </Link>
          <Link
            to={
              dateFrom && dateTo
                ? `/rentals/${item.id}?action=book&from=${dateFrom}&to=${dateTo}`
                : `/rentals/${item.id}?action=book`
            }
          >
            <Button size="sm" className="bg-orange-500 hover:bg-orange-600" data-testid={`book-equipment-${item.id}`}>
              {t('rentals.book')}
            </Button>
          </Link>
        </div>
      </CardFooter>
    </Card>
  );
};

// Compute availability state for a listing given an optional [from, to] range.
// Returns { state: 'available' | 'available_from' | 'unknown' | 'unavailable',
//           date?: 'YYYY-MM-DD' }.
//
// Honest fallback: a listing without any availability metadata is reported
// as `unknown` so the UI can label it "Saadavus täpsustamisel" rather
// than lying that it's free.
const computeAvailability = (item, from, to) => {
  const status = item?.status;
  if (status && status !== 'available' && status !== 'active') {
    return { state: 'unavailable' };
  }

  const mode = item?.availability_mode;
  const windows = Array.isArray(item?.availability_windows) ? item.availability_windows : [];
  const availFrom = item?.available_from;

  if (!mode && windows.length === 0 && !availFrom) {
    return { state: 'unknown' };
  }

  if (mode === 'always' || (!from && !to && (!availFrom || availFrom <= new Date().toISOString().slice(0, 10)))) {
    return { state: 'available' };
  }

  if (from && to) {
    if (windows.length > 0) {
      const fitsAny = windows.some((w) => {
        const ws = w?.start;
        const we = w?.end;
        return ws && we && ws <= from && we >= to;
      });
      if (fitsAny) return { state: 'available' };
      const earliest = windows
        .map((w) => w?.start)
        .filter((d) => d && d > from)
        .sort()[0];
      if (earliest) return { state: 'available_from', date: earliest };
      return { state: 'unavailable' };
    }
    if (availFrom) {
      if (availFrom <= from) return { state: 'available' };
      return { state: 'available_from', date: availFrom };
    }
  }

  if (availFrom && availFrom > new Date().toISOString().slice(0, 10)) {
    return { state: 'available_from', date: availFrom };
  }
  return { state: 'available' };
};

const AvailabilityBadge = ({ state, date, t }) => {
  if (state === 'unavailable') {
    return (
      <Badge variant="secondary" data-testid="availability-unavailable">
        {t('rentals.unavailable')}
      </Badge>
    );
  }
  if (state === 'available_from') {
    return (
      <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100" data-testid="availability-from">
        {t('rentals.availableFrom')} {date}
      </Badge>
    );
  }
  if (state === 'unknown') {
    return (
      <Badge variant="outline" className="border-slate-300 text-slate-600" data-testid="availability-unknown">
        {t('rentals.availabilityTbd')}
      </Badge>
    );
  }
  return (
    <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100" data-testid="availability-available">
      {t('rentals.available')}
    </Badge>
  );
};

// =============================================================================
// VehiclesPanel — embedded inline on /rentals?vertical=vehicles.
//
// Why a separate component? The equipment listing UI is built around tools
// (categories like power_tools, construction, welding…). Vehicles need a
// completely different shape: vehicle type → model picker → logistics-grade
// extras (4x4, tail-lift, ADR…) → matched providers. We reuse the existing
// /api/rentals/vehicle/{catalog,request} endpoints (battle-tested by
// VehicleRentalRequestPage + 13 backend pytest cases).
// =============================================================================
const VEHICLE_COUNTRY_OPTIONS = ['NO', 'EE', 'FI', 'SE', 'DK', 'DE', 'PL'];

// Currency formatter — matches the locale we surface in the card price row.
const fmtMoney = (v, ccy) => {
  if (v === null || v === undefined || v === '') return '—';
  return `${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })} ${ccy || ''}`.trim();
};

// Browse-mode VehiclesPanel — shows the seeded `vehicle_rental_items` as
// Hilti/Finn.no-style cards with filters & sort. The capability-tag
// REQUEST flow (`/rentals/vehicle/request`) remains accessible via the
// "Ei leia sobivat? Saada täpne päring" link at the bottom.
const VehiclesPanel = ({ initialCountry }) => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();

  const [country, setCountry] = useState(initialCountry || searchParams.get('country') || 'NO');
  const [types, setTypes] = useState([]); // multi-select vehicle_type
  const [allTypes, setAllTypes] = useState([]);
  const [payloadMin, setPayloadMin] = useState('');
  const [volumeMin, setVolumeMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [flagLiftgate, setFlagLiftgate] = useState(false);
  const [flagFridge, setFlagFridge] = useState(false);
  const [flag4x4, setFlag4x4] = useState(false);
  const [flagDriver, setFlagDriver] = useState(false);
  const [sort, setSort] = useState('newest');

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API = process.env.REACT_APP_BACKEND_URL;

  // Load distinct vehicle types once for the filter chip row.
  useEffect(() => {
    fetch(`${API}/api/rentals/vehicles/types`)
      .then((r) => r.json())
      .then((d) => setAllTypes(d?.types || []))
      .catch(() => setAllTypes([]));
  }, [API]);

  const fetchInventory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (country) params.set('country', country);
      if (types.length) params.set('vehicle_type', types.join(','));
      if (payloadMin) params.set('payload_min', payloadMin);
      if (volumeMin) params.set('volume_min', volumeMin);
      if (priceMax) params.set('price_max', priceMax);
      if (flagLiftgate) params.set('liftgate', 'true');
      if (flagFridge) params.set('refrigerated', 'true');
      if (flag4x4) params.set('four_wheel_drive', 'true');
      if (flagDriver) params.set('driver_included', 'true');
      params.set('sort', sort);
      params.set('limit', '24');
      const res = await fetch(`${API}/api/rentals/vehicles?${params.toString()}`);
      const data = await res.json();
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotal(data?.total || 0);
    } catch (e) {
      setError(String(e));
      setItems([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [API, country, types, payloadMin, volumeMin, priceMax,
      flagLiftgate, flagFridge, flag4x4, flagDriver, sort]);

  useEffect(() => { fetchInventory(); }, [fetchInventory]);

  const toggleType = (t_) => {
    setTypes((prev) => prev.includes(t_) ? prev.filter(x => x !== t_) : [...prev, t_]);
  };

  return (
    <div className="space-y-5" data-testid="vehicles-panel">
      {/* Filter card */}
      <Card data-testid="vehicles-filters">
        <CardContent className="pt-6 space-y-4">
          {/* Row 1: country + sort + total count */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
            <div>
              <label className="text-xs font-medium text-slate-700">{t('vehicleRental.country')}</label>
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger data-testid="vehicles-country"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {VEHICLE_COUNTRY_OPTIONS.map(c => (
                    <SelectItem key={c} value={c}>{t(`countries.${c}`, { defaultValue: c })}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">{t('vehicleRental.inline.sort')}</label>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger data-testid="vehicles-sort"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">{t('vehicleRental.inline.sortNewest')}</SelectItem>
                  <SelectItem value="cheapest">{t('vehicleRental.inline.sortCheapest')}</SelectItem>
                  <SelectItem value="highest_payload">{t('vehicleRental.inline.sortPayload')}</SelectItem>
                  <SelectItem value="best_rating">{t('vehicleRental.inline.sortRating')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2 text-xs text-slate-600 self-end pb-2 text-right">
              {loading ? t('common.loading') : t('vehicleRental.inline.foundCount', { count: total, defaultValue: `${total}` })}
            </div>
          </div>

          {/* Row 2: vehicle_type multi-select chips */}
          {allTypes.length > 0 && (
            <div>
              <div className="text-xs font-medium text-slate-700 mb-1.5">
                {t('vehicleRental.inline.typeFilter')}
              </div>
              <div className="flex flex-wrap gap-1.5" data-testid="vehicles-type-chips">
                {allTypes.map((vt) => {
                  const active = types.includes(vt);
                  return (
                    <button
                      key={vt}
                      type="button"
                      onClick={() => toggleType(vt)}
                      data-testid={`vehicles-type-chip-${vt}`}
                      className={`text-xs font-medium px-2.5 py-1 rounded-full border transition ${
                        active
                          ? 'bg-orange-500 border-orange-500 text-white'
                          : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      {t(`vehicleRental.types.${vt}`, { defaultValue: vt.replace(/_/g, ' ') })}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Row 3: numeric filters */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-700">{t('vehicleRental.inline.payloadMin')}</label>
              <Input type="number" min={0} step={50} value={payloadMin}
                     onChange={(e) => setPayloadMin(e.target.value)} placeholder="kg"
                     data-testid="vehicles-payload-min" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">{t('vehicleRental.inline.volumeMin')}</label>
              <Input type="number" min={0} step={1} value={volumeMin}
                     onChange={(e) => setVolumeMin(e.target.value)} placeholder="m³"
                     data-testid="vehicles-volume-min" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">{t('vehicleRental.inline.priceMax')}</label>
              <Input type="number" min={0} step={50} value={priceMax}
                     onChange={(e) => setPriceMax(e.target.value)} placeholder="EUR/NOK"
                     data-testid="vehicles-price-max" />
            </div>
            <div className="flex items-end">
              {(types.length || payloadMin || volumeMin || priceMax || flagLiftgate || flagFridge || flag4x4 || flagDriver) ? (
                <button type="button"
                        onClick={() => {
                          setTypes([]); setPayloadMin(''); setVolumeMin('');
                          setPriceMax(''); setFlagLiftgate(false); setFlagFridge(false);
                          setFlag4x4(false); setFlagDriver(false);
                        }}
                        className="text-xs text-slate-500 hover:text-slate-900 underline pb-2"
                        data-testid="vehicles-filters-clear">
                  {t('rentals.specFilters.clear')}
                </button>
              ) : null}
            </div>
          </div>

          {/* Row 4: feature toggles */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
              ['liftgate',  flagLiftgate, setFlagLiftgate],
              ['refrigerated', flagFridge, setFlagFridge],
              ['four_wheel_drive', flag4x4, setFlag4x4],
              ['driver_included', flagDriver, setFlagDriver],
            ].map(([k, val, setter]) => (
              <label key={k}
                     data-testid={`vehicles-flag-${k}`}
                     className={`flex items-center gap-2 text-sm cursor-pointer rounded border px-2 py-1.5 transition ${
                       val ? 'border-orange-300 bg-orange-50 text-orange-900'
                          : 'border-slate-200 hover:bg-slate-50'
                     }`}>
                <input type="checkbox" checked={val}
                       onChange={(e) => setter(e.target.checked)}
                       className="h-4 w-4 accent-orange-500" />
                <span>{t(`vehicleRental.flags.${k}`, { defaultValue: k })}</span>
              </label>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Results grid */}
      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="vehicles-loading">
          {[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-72" />)}
        </div>
      ) : error ? (
        <Card><CardContent className="py-12 text-center text-rose-600" data-testid="vehicles-error">
          {error}
        </CardContent></Card>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-12 text-center" data-testid="vehicles-empty">
          <Truck className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-50" />
          <div className="font-medium">{t('vehicleRental.inline.noResultsTitle')}</div>
          <div className="text-sm text-muted-foreground mt-1">{t('vehicleRental.inline.noResultsHelp')}</div>
        </CardContent></Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="vehicles-grid">
          {items.map((v) => (
            <Card key={v.id} className="overflow-hidden hover:shadow-lg transition"
                  data-testid={`vehicle-card-${v.id}`}>
              <div className="relative aspect-[4/3] bg-slate-100">
                <img src={v.photos?.[0]} alt={v.title}
                     className="absolute inset-0 w-full h-full object-cover"
                     onError={(e) => { e.target.style.display = 'none'; }} />
                {v.is_demo && (
                  <Badge className="absolute top-2 left-2 bg-amber-500 text-white border-0">DEMO</Badge>
                )}
                <Badge className="absolute top-2 right-2 bg-white text-slate-900 border border-slate-200">
                  {t(`vehicleRental.types.${v.vehicle_type}`, { defaultValue: v.vehicle_type })}
                </Badge>
              </div>
              <CardContent className="p-4 space-y-2">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-orange-700 font-bold">
                    {v.brand}
                  </div>
                  <div className="font-semibold text-slate-900 leading-tight">{v.model}</div>
                </div>
                <div className="text-xs text-slate-500 flex items-center gap-1">
                  <MapPin className="h-3 w-3" /> {v.city}, {v.country}
                  {typeof v.rating_avg === 'number' && v.rating_avg > 0 && (
                    <span className="ml-2 inline-flex items-center gap-0.5">
                      <Star className="h-3 w-3 text-amber-500" /> {v.rating_avg.toFixed(1)}
                      <span className="text-slate-400">({v.rating_count})</span>
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-1 text-[11px]">
                  {v.payload_kg ? (
                    <span className="rounded bg-slate-100 px-1.5 py-0.5">{v.payload_kg} kg</span>
                  ) : null}
                  {v.volume_m3 ? (
                    <span className="rounded bg-slate-100 px-1.5 py-0.5">{v.volume_m3} m³</span>
                  ) : null}
                  {v.license_required ? (
                    <span className="rounded bg-slate-100 px-1.5 py-0.5">{v.license_required}</span>
                  ) : null}
                  {v.driver_included && (
                    <span className="rounded bg-emerald-100 text-emerald-800 px-1.5 py-0.5">
                      {t('vehicleRental.flags.driver_included')}
                    </span>
                  )}
                  {v.flags?.liftgate && (
                    <span className="rounded bg-blue-100 text-blue-800 px-1.5 py-0.5">
                      {t('vehicleRental.flags.liftgate')}
                    </span>
                  )}
                  {v.flags?.refrigerated && (
                    <span className="rounded bg-cyan-100 text-cyan-800 px-1.5 py-0.5">
                      {t('vehicleRental.flags.refrigerated')}
                    </span>
                  )}
                  {v.flags?.four_wheel_drive && (
                    <span className="rounded bg-amber-100 text-amber-800 px-1.5 py-0.5">
                      4×4
                    </span>
                  )}
                </div>
                <div className="flex items-end justify-between pt-1 border-t">
                  <div>
                    <div className="text-lg font-bold text-slate-900">
                      {fmtMoney(v.price_amount, v.currency)}
                    </div>
                    <div className="text-[11px] text-slate-500">
                      / {t('rentals.day', { defaultValue: 'päev' })}
                      {v.deposit_amount ? ` · ${t('rentals.deposit', { defaultValue: 'tagatis' })} ${fmtMoney(v.deposit_amount, v.currency)}` : ''}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {v.delivery_available && (
                      <Truck className="h-4 w-4 text-emerald-600" title="Tarne" />
                    )}
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Link to={`/rentals/vehicle/${v.id}`} className="flex-1"
                        data-testid={`vehicle-card-view-${v.id}`}>
                    <Button variant="outline" size="sm" className="w-full">
                      {t('rentals.view', { defaultValue: 'Vaata' })}
                    </Button>
                  </Link>
                  <Link to={`/rentals/vehicle/${v.id}?action=book`} className="flex-1"
                        data-testid={`vehicle-card-book-${v.id}`}>
                    <Button size="sm" className="w-full bg-orange-500 hover:bg-orange-600">
                      {t('rentals.book')}
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Footer: link to capability-tag REQUEST wizard for hard-to-find specs */}
      <div className="rounded-lg border border-dashed bg-slate-50 p-4 text-center"
           data-testid="vehicles-request-cta">
        <div className="text-sm text-slate-700 font-medium">
          {t('vehicleRental.inline.cantFindTitle')}
        </div>
        <div className="text-xs text-slate-500 mt-0.5 mb-2">
          {t('vehicleRental.inline.cantFindHelp')}
        </div>
        <Link to={`/rentals/vehicle/request?country=${country}`}
              data-testid="vehicles-open-request-wizard">
          <Button variant="outline" size="sm">
            {t('vehicleRental.inline.openRequestWizard')}
          </Button>
        </Link>
      </div>
    </div>
  );
};

// =============================================================================
// EquipmentPanel — heavy machinery (forklifts/lifts/excavators/etc.).
// Mirrors VehiclesPanel layout so the user gets a consistent rental UX, but
// the taxonomy and filters are equipment-specific (capacity_kg / lift_m /
// power_kW / operator_included). Backed by /api/rentals/equipment.
// =============================================================================
const EQUIPMENT_COUNTRY_OPTIONS = ['NO', 'EE', 'FI', 'SE', 'DK', 'DE', 'PL'];

const EquipmentPanel = ({ initialCountry }) => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();

  const [country, setCountry] = useState(initialCountry || searchParams.get('country') || 'NO');
  const [types, setTypes] = useState([]);
  const [allTypes, setAllTypes] = useState([]);
  const [capacityMin, setCapacityMin] = useState('');
  const [liftMin, setLiftMin] = useState('');
  const [powerMin, setPowerMin] = useState('');
  const [operatorOnly, setOperatorOnly] = useState(false);
  const [deliveryOnly, setDeliveryOnly] = useState(false);
  const [priceMax, setPriceMax] = useState('');
  const [sort, setSort] = useState('newest');

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetch(`${API}/api/rentals/equipment/types`)
      .then((r) => r.json())
      .then((d) => setAllTypes(d?.types || []))
      .catch(() => setAllTypes([]));
  }, [API]);

  const fetchInventory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (country) params.set('country', country);
      if (types.length) params.set('equipment_type', types.join(','));
      if (capacityMin) params.set('capacity_min', capacityMin);
      if (liftMin) params.set('lift_min', liftMin);
      if (powerMin) params.set('power_min', powerMin);
      if (operatorOnly) params.set('operator_included', 'true');
      if (deliveryOnly) params.set('delivery_available', 'true');
      if (priceMax) params.set('price_max', priceMax);
      params.set('sort', sort);
      params.set('limit', '24');
      const res = await fetch(`${API}/api/rentals/equipment?${params.toString()}`);
      const data = await res.json();
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotal(data?.total || 0);
    } catch (e) {
      setError(String(e));
      setItems([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [API, country, types, capacityMin, liftMin, powerMin,
      operatorOnly, deliveryOnly, priceMax, sort]);

  useEffect(() => { fetchInventory(); }, [fetchInventory]);

  const toggleType = (t_) => {
    setTypes((prev) => prev.includes(t_) ? prev.filter(x => x !== t_) : [...prev, t_]);
  };

  return (
    <div className="space-y-5" data-testid="equipment-panel">
      <Card data-testid="equipment-filters">
        <CardContent className="pt-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
            <div>
              <label className="text-xs font-medium text-slate-700">{t('vehicleRental.country')}</label>
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger data-testid="equipment-country"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EQUIPMENT_COUNTRY_OPTIONS.map(c => (
                    <SelectItem key={c} value={c}>{t(`countries.${c}`, { defaultValue: c })}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">{t('vehicleRental.inline.sort')}</label>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger data-testid="equipment-sort"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">{t('vehicleRental.inline.sortNewest')}</SelectItem>
                  <SelectItem value="cheapest">{t('vehicleRental.inline.sortCheapest')}</SelectItem>
                  <SelectItem value="highest_capacity">{t('equipmentRental.inline.sortCapacity')}</SelectItem>
                  <SelectItem value="highest_lift">{t('equipmentRental.inline.sortLift')}</SelectItem>
                  <SelectItem value="best_rating">{t('vehicleRental.inline.sortRating')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2 text-xs text-slate-600 self-end pb-2 text-right">
              {loading ? t('common.loading') : t('equipmentRental.inline.foundCount', { count: total, defaultValue: `${total}` })}
            </div>
          </div>

          {allTypes.length > 0 && (
            <div>
              <div className="text-xs font-medium text-slate-700 mb-1.5">
                {t('equipmentRental.inline.typeFilter')}
              </div>
              <div className="flex flex-wrap gap-1.5" data-testid="equipment-type-chips">
                {allTypes.map((et) => {
                  const active = types.includes(et);
                  return (
                    <button
                      key={et}
                      type="button"
                      onClick={() => toggleType(et)}
                      data-testid={`equipment-type-chip-${et}`}
                      className={`text-xs font-medium px-2.5 py-1 rounded-full border transition ${
                        active
                          ? 'bg-orange-500 border-orange-500 text-white'
                          : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      {t(`equipmentRental.types.${et}`, { defaultValue: et.replace(/_/g, ' ') })}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-700">{t('equipmentRental.inline.capacityMin')}</label>
              <Input type="number" min={0} step={50} value={capacityMin}
                     onChange={(e) => setCapacityMin(e.target.value)} placeholder="kg"
                     data-testid="equipment-capacity-min" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">{t('equipmentRental.inline.liftMin')}</label>
              <Input type="number" min={0} step={1} value={liftMin}
                     onChange={(e) => setLiftMin(e.target.value)} placeholder="m"
                     data-testid="equipment-lift-min" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">{t('equipmentRental.inline.powerMin')}</label>
              <Input type="number" min={0} step={1} value={powerMin}
                     onChange={(e) => setPowerMin(e.target.value)} placeholder="kW"
                     data-testid="equipment-power-min" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">{t('vehicleRental.inline.priceMax')}</label>
              <Input type="number" min={0} step={50} value={priceMax}
                     onChange={(e) => setPriceMax(e.target.value)} placeholder="EUR/NOK"
                     data-testid="equipment-price-max" />
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <label className={`flex items-center gap-2 text-sm cursor-pointer rounded border px-2 py-1.5 transition ${
                  operatorOnly ? 'border-orange-300 bg-orange-50 text-orange-900'
                               : 'border-slate-200 hover:bg-slate-50'}`}
                  data-testid="equipment-flag-operator">
              <input type="checkbox" checked={operatorOnly}
                     onChange={(e) => setOperatorOnly(e.target.checked)}
                     className="h-4 w-4 accent-orange-500" />
              <span>{t('equipmentRental.flags.operator_included')}</span>
            </label>
            <label className={`flex items-center gap-2 text-sm cursor-pointer rounded border px-2 py-1.5 transition ${
                  deliveryOnly ? 'border-orange-300 bg-orange-50 text-orange-900'
                               : 'border-slate-200 hover:bg-slate-50'}`}
                  data-testid="equipment-flag-delivery">
              <input type="checkbox" checked={deliveryOnly}
                     onChange={(e) => setDeliveryOnly(e.target.checked)}
                     className="h-4 w-4 accent-orange-500" />
              <span>{t('equipmentRental.flags.delivery_available')}</span>
            </label>
            {(types.length || capacityMin || liftMin || powerMin || operatorOnly || deliveryOnly || priceMax) ? (
              <button type="button"
                      onClick={() => {
                        setTypes([]); setCapacityMin(''); setLiftMin(''); setPowerMin('');
                        setOperatorOnly(false); setDeliveryOnly(false); setPriceMax('');
                      }}
                      className="text-xs text-slate-500 hover:text-slate-900 underline"
                      data-testid="equipment-filters-clear">
                {t('rentals.specFilters.clear')}
              </button>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="equipment-loading">
          {[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-72" />)}
        </div>
      ) : error ? (
        <Card><CardContent className="py-12 text-center text-rose-600" data-testid="equipment-error">
          {error}
        </CardContent></Card>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-12 text-center" data-testid="equipment-empty">
          <Wrench className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-50" />
          <div className="font-medium">{t('equipmentRental.inline.noResultsTitle')}</div>
          <div className="text-sm text-muted-foreground mt-1">{t('equipmentRental.inline.noResultsHelp')}</div>
        </CardContent></Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="equipment-grid">
          {items.map((e) => (
            <Card key={e.id} className="overflow-hidden hover:shadow-lg transition"
                  data-testid={`equipment-card-${e.id}`}>
              <div className="relative aspect-[4/3] bg-slate-100">
                <img src={e.photos?.[0]} alt={e.title}
                     className="absolute inset-0 w-full h-full object-cover"
                     onError={(ev) => { ev.target.style.display = 'none'; }} />
                {e.is_demo && (
                  <Badge className="absolute top-2 left-2 bg-amber-500 text-white border-0">DEMO</Badge>
                )}
                <Badge className="absolute top-2 right-2 bg-white text-slate-900 border border-slate-200">
                  {t(`equipmentRental.types.${e.equipment_type}`, { defaultValue: e.equipment_type })}
                </Badge>
              </div>
              <CardContent className="p-4 space-y-2">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-orange-700 font-bold">
                    {e.brand}
                  </div>
                  <div className="font-semibold text-slate-900 leading-tight">{e.model}</div>
                </div>
                <div className="text-xs text-slate-500 flex items-center gap-1">
                  <MapPin className="h-3 w-3" /> {e.city}, {e.country}
                  {typeof e.rating_avg === 'number' && e.rating_avg > 0 && (
                    <span className="ml-2 inline-flex items-center gap-0.5">
                      <Star className="h-3 w-3 text-amber-500" /> {e.rating_avg.toFixed(1)}
                      <span className="text-slate-400">({e.rating_count})</span>
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-1 text-[11px]">
                  {e.capacity_kg ? (
                    <span className="rounded bg-slate-100 px-1.5 py-0.5">{e.capacity_kg} kg</span>
                  ) : null}
                  {e.lift_height_m ? (
                    <span className="rounded bg-slate-100 px-1.5 py-0.5">{e.lift_height_m} m</span>
                  ) : null}
                  {e.power_kw ? (
                    <span className="rounded bg-slate-100 px-1.5 py-0.5">{e.power_kw} kW</span>
                  ) : null}
                  {e.operator_included && (
                    <span className="rounded bg-emerald-100 text-emerald-800 px-1.5 py-0.5">
                      {t('equipmentRental.flags.operator_included')}
                    </span>
                  )}
                </div>
                <div className="flex items-end justify-between pt-1 border-t">
                  <div>
                    <div className="text-lg font-bold text-slate-900">
                      {fmtMoney(e.price_amount, e.currency)}
                    </div>
                    <div className="text-[11px] text-slate-500">
                      / {t('rentals.day', { defaultValue: 'day' })}
                      {e.deposit_amount ? ` · ${t('rentals.deposit', { defaultValue: 'deposit' })} ${fmtMoney(e.deposit_amount, e.currency)}` : ''}
                    </div>
                  </div>
                  {e.delivery_available && (
                    <Truck className="h-4 w-4 text-emerald-600" />
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// Main Equipment List Page
export const EquipmentRentals = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [equipment, setEquipment] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [search, setSearch] = useState(searchParams.get('search'));
  const [category, setCategory] = useState(searchParams.get('category'));
  const [country, setCountry] = useState(searchParams.get('country'));
  // Date-range availability window (YYYY-MM-DD; empty = no constraint)
  const [dateFrom, setDateFrom] = useState(searchParams.get('from') || '');
  const [dateTo, setDateTo] = useState(searchParams.get('to') || '');
  const dateError = dateFrom && dateTo && dateFrom > dateTo;
  const page = parseInt(searchParams.get('page')) || 1;

  // Spec filters (Tools vertical) — client-side filter against item.specs[].
  // Pro users want to find a generator by minimum kW, a welder by minimum A,
  // a drill/impact by minimum W. Empty string = no constraint.
  const [minKw, setMinKw] = useState(searchParams.get('minkw') || '');
  const [minAmp, setMinAmp] = useState(searchParams.get('mina') || '');
  const [minWatt, setMinWatt] = useState(searchParams.get('minw') || '');

  // Vertical separator: ?vertical=tools | ?vertical=vehicles | (default = all).
  // Drives default category filter, header copy, and tab UI.
  const vertical = searchParams.get('vertical') || 'all';
  const VEHICLE_CATEGORY_IDS = ['vehicles'];
  const TOOL_CATEGORY_IDS = [
    'power_tools', 'hand_tools', 'construction', 'garden',
    'cleaning', 'lifting', 'safety', 'welding', 'other',
  ];
  const verticalCategoryIds =
    vertical === 'vehicles' ? VEHICLE_CATEGORY_IDS :
    vertical === 'tools' ? TOOL_CATEGORY_IDS : null;

  const fetchEquipment = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page,
        limit: 12,
        ...(search && { search }),
        ...(category && category !== 'all' && { category }),
        ...(country && { country })
      };
      
      const res = await api.getRentalItems(params);
      // Defensive: safely extract items and total
      const data = res?.data;
      let items = [];
      let totalCount = 0;
      
      if (data) {
        if (Array.isArray(data)) {
          items = data;
          totalCount = data.length;
        } else if (typeof data === 'object') {
          items = Array.isArray(data.items) ? data.items : [];
          totalCount = typeof data.total === 'number' ? data.total : items.length;
        }
      }
      
      setEquipment(items);
      setTotal(totalCount);
    } catch (err) {
      console.error('Failed to fetch equipment:', err);
      setError(t('rentals.unableToLoadEquipment'));
      setEquipment([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, search, category, country, t]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await api.getRentalCategories();
      // Defensive: ensure categories is always an array
      const cats = res?.data?.categories;
      setCategories(Array.isArray(cats) ? cats : []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
      setCategories([]);
    }
  }, []);

  useEffect(() => {
    fetchEquipment();
    fetchCategories();
  }, [fetchEquipment, fetchCategories]);

  const handleSearch = () => {
    if (dateError) return;
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (category && category !== 'all') params.set('category', category);
    if (country) params.set('country', country);
    if (dateFrom) params.set('from', dateFrom);
    if (dateTo) params.set('to', dateTo);
    if (minKw) params.set('minkw', minKw);
    if (minAmp) params.set('mina', minAmp);
    if (minWatt) params.set('minw', minWatt);
    params.set('page', '1');
    setSearchParams(params);
  };

  // Client-side date filter: hides items that are explicitly UNAVAILABLE for
  // the chosen window. `unknown` items are kept visible (with honest label).
  const visibleEquipment = useMemo(() => {
    let list = equipment;
    if (verticalCategoryIds) {
      list = list.filter((it) => verticalCategoryIds.includes(it.category));
    }
    // Spec-min filters: drop items whose matching spec value is below the
    // requested minimum. Items with NO spec for the requested unit are
    // dropped too (a kW filter implicitly targets generators/construction).
    const specMin = (item, unit) => {
      const row = (item.specs || []).find(
        (s) => (s.unit || '').toLowerCase() === unit.toLowerCase()
              || (s.key || '').toLowerCase() === unit.toLowerCase(),
      );
      const v = row && parseFloat(String(row.value).replace(',', '.'));
      return Number.isFinite(v) ? v : null;
    };
    const filterByMin = (l, raw, unit) => {
      const min = parseFloat(raw);
      if (!Number.isFinite(min) || min <= 0) return l;
      return l.filter((it) => {
        const v = specMin(it, unit);
        return v !== null && v >= min;
      });
    };
    list = filterByMin(list, minKw, 'kW');
    list = filterByMin(list, minAmp, 'A');
    list = filterByMin(list, minWatt, 'W');
    if (!dateFrom || !dateTo) return list;
    return list.filter((it) => {
      const a = computeAvailability(it, dateFrom, dateTo);
      return a.state !== 'unavailable';
    });
  }, [equipment, dateFrom, dateTo, verticalCategoryIds, minKw, minAmp, minWatt]);

  return (
    <div className="min-h-screen bg-background">
      <RentalsNav />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="equipment-rentals-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold">
              {vertical === 'vehicles'
                ? t('vehicleRental.inline.headerTitle')
                : vertical === 'equipment'
                ? t('equipmentRental.inline.headerTitle')
                : t('rentals.title')}
            </h1>
            <p className="text-muted-foreground">
              {vertical === 'vehicles'
                ? t('vehicleRental.inline.headerSubtitle')
                : vertical === 'equipment'
                ? t('equipmentRental.inline.headerSubtitle')
                : t('rentals.subtitle')}
            </p>
          </div>
          
          {user && vertical !== 'vehicles' && vertical !== 'equipment' && (
            <Link to="/rentals/new">
              <Button data-testid="list-equipment-btn">
                <Plus className="mr-2 h-4 w-4" />
                {t('rentals.listEquipment')}
              </Button>
            </Link>
          )}
        </div>

        {/* Vertical tabs: All · Tools · Equipment · Vehicles */}
        <div className="mb-6 flex gap-2 border-b border-slate-200 overflow-x-auto" data-testid="rentals-vertical-tabs">
          {[
            { id: 'all',       label: t('rentals.verticals.all'),       icon: Package },
            { id: 'tools',     label: t('rentals.verticals.tools'),     icon: Wrench },
            { id: 'equipment', label: t('rentals.verticals.equipment'), icon: Wrench },
            { id: 'vehicles',  label: t('rentals.verticals.vehicles'),  icon: Truck },
          ].map((tab) => {
            const Icon = tab.icon;
            const active = vertical === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  const params = new URLSearchParams(searchParams);
                  if (tab.id === 'all') params.delete('vertical');
                  else params.set('vertical', tab.id);
                  params.set('page', '1');
                  setSearchParams(params);
                }}
                className={`-mb-px flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition ${
                  active
                    ? 'border-orange-500 text-orange-700'
                    : 'border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300'
                }`}
                data-testid={`rentals-vertical-${tab.id}`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Filters */}
        {vertical === 'vehicles' ? (
          <VehiclesPanel
            initialCountry={country || 'NO'}
            initialDateFrom={dateFrom}
            initialDateTo={dateTo}
          />
        ) : vertical === 'equipment' ? (
          <EquipmentPanel initialCountry={country || 'NO'} />
        ) : (
        <>
        <Card className="mb-8">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="sm:col-span-2">
                <Input
                  placeholder={t('rentals.searchPlaceholder')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  data-testid="equipment-search-input"
                />
              </div>
              
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="category-filter">
                  <SelectValue placeholder={t('rentals.categoryPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('rentals.allCategories')}</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {categoryIcons[cat.id]} {(() => {
                        const key = `categories.${cat.id}`;
                        const translated = t(key);
                        return translated !== key ? translated : cat.name;
                      })()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button onClick={handleSearch} data-testid="search-btn" disabled={!!dateError}>
                <Search className="mr-2 h-4 w-4" />
                {t('common.search')}
              </Button>
            </div>

            {/* Date range — drives the availability badge on every card */}
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1" htmlFor="rental-date-from">
                  {t('rentals.dateFromLabel')}
                </label>
                <Input
                  id="rental-date-from"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  data-testid="rental-date-from"
                  min={new Date().toISOString().slice(0, 10)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1" htmlFor="rental-date-to">
                  {t('rentals.dateToLabel')}
                </label>
                <Input
                  id="rental-date-to"
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  data-testid="rental-date-to"
                  min={dateFrom || new Date().toISOString().slice(0, 10)}
                />
              </div>
              <div className="text-xs text-muted-foreground">
                {dateError ? (
                  <span className="text-red-600 font-medium" data-testid="rental-date-error">
                    {t('rentals.dateRangeError')}
                  </span>
                ) : dateFrom && dateTo ? (
                  <span data-testid="rental-date-window-applied">
                    {`${dateFrom} → ${dateTo}`}
                  </span>
                ) : (
                  <span>{t('rentals.dateRangeHelp')}</span>
                )}
              </div>
            </div>

            {/* Pro spec filters — only relevant on Tools vertical (or All).
                Hidden on Vehicles since that vertical has its own picker. */}
            {vertical !== 'vehicles' && (
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 items-end"
                   data-testid="rentals-spec-filters">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1"
                         htmlFor="rental-min-kw">
                    {t('rentals.specFilters.minKW')}
                  </label>
                  <Input
                    id="rental-min-kw"
                    type="number" min={0} step={0.5} value={minKw}
                    onChange={(e) => setMinKw(e.target.value)}
                    placeholder="kW"
                    data-testid="rental-min-kw"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1"
                         htmlFor="rental-min-a">
                    {t('rentals.specFilters.minA')}
                  </label>
                  <Input
                    id="rental-min-a"
                    type="number" min={0} step={10} value={minAmp}
                    onChange={(e) => setMinAmp(e.target.value)}
                    placeholder="A"
                    data-testid="rental-min-a"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1"
                         htmlFor="rental-min-w">
                    {t('rentals.specFilters.minW')}
                  </label>
                  <Input
                    id="rental-min-w"
                    type="number" min={0} step={50} value={minWatt}
                    onChange={(e) => setMinWatt(e.target.value)}
                    placeholder="W"
                    data-testid="rental-min-w"
                  />
                </div>
                {(minKw || minAmp || minWatt) && (
                  <button
                    type="button"
                    onClick={() => {
                      setMinKw(''); setMinAmp(''); setMinWatt('');
                      const params = new URLSearchParams(searchParams);
                      params.delete('minkw'); params.delete('mina'); params.delete('minw');
                      setSearchParams(params);
                    }}
                    className="text-xs text-slate-500 hover:text-slate-900 underline"
                    data-testid="rentals-spec-clear"
                  >
                    {t('rentals.specFilters.clear')}
                  </button>
                )}
              </div>
            )}

            {/* Quick filter chips — Hilti/Finn.no-style 1-tap availability check */}
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  const today = new Date().toISOString().slice(0, 10);
                  const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
                  setDateFrom(today);
                  setDateTo(tomorrow);
                  const params = new URLSearchParams(searchParams);
                  params.set('from', today);
                  params.set('to', tomorrow);
                  params.set('page', '1');
                  setSearchParams(params);
                }}
                className="inline-flex items-center gap-1.5 rounded-full border border-orange-300 bg-orange-50 px-3 py-1.5 text-xs font-medium text-orange-800 hover:bg-orange-100 transition"
                data-testid="quick-filter-available-today"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                {t('rentals.availableToday')}
              </button>
              <button
                type="button"
                onClick={() => {
                  const today = new Date();
                  const fri = new Date(today);
                  // next Friday
                  const daysToFri = (5 - today.getDay() + 7) % 7 || 7;
                  fri.setDate(today.getDate() + daysToFri);
                  const sun = new Date(fri);
                  sun.setDate(fri.getDate() + 2);
                  const f = fri.toISOString().slice(0, 10);
                  const s = sun.toISOString().slice(0, 10);
                  setDateFrom(f);
                  setDateTo(s);
                  const params = new URLSearchParams(searchParams);
                  params.set('from', f);
                  params.set('to', s);
                  params.set('page', '1');
                  setSearchParams(params);
                }}
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 transition"
                data-testid="quick-filter-this-weekend"
              >
                {t('rentals.thisWeekend')}
              </button>
              {(dateFrom || dateTo) && (
                <button
                  type="button"
                  onClick={() => {
                    setDateFrom('');
                    setDateTo('');
                    const params = new URLSearchParams(searchParams);
                    params.delete('from');
                    params.delete('to');
                    setSearchParams(params);
                  }}
                  className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-500 hover:bg-slate-50 transition"
                  data-testid="quick-filter-clear-dates"
                >
                  {t('rentals.clearDates')}
                </button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Equipment Grid */}
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="equipment-loading">
            {[1,2,3,4,5,6,7,8].map(i => (
              <Skeleton key={i} className="h-80" />
            ))}
          </div>
        ) : error ? (
          <Card data-testid="equipment-error">
            <CardContent className="py-16 text-center">
              <Package className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-xl font-semibold mb-2">{t('common.somethingWentWrong')}</h3>
              <p className="text-muted-foreground mb-4">
                {error}
              </p>
              <Button variant="outline" onClick={fetchEquipment}>
                {t('common.tryAgain')}
              </Button>
            </CardContent>
          </Card>
        ) : !visibleEquipment || visibleEquipment.length === 0 ? (
          <Card data-testid="equipment-empty">
            <CardContent className="py-16 text-center">
              <Package className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-xl font-semibold mb-2">
                {dateFrom && dateTo ? t('rentals.noEquipmentInWindow') : t('rentals.noEquipment')}
              </h3>
              <p className="text-muted-foreground mb-4">
                {dateFrom && dateTo ? t('rentals.noEquipmentInWindowDesc') : t('rentals.noEquipmentDesc')}
              </p>
              {user && (
                <Link to="/rentals/new">
                  <Button>
                    <Plus className="mr-2 h-4 w-4" />
                    {t('rentals.listYourEquipment')}
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {visibleEquipment.map((item) => (
                <EquipmentCard key={item.id} item={item} dateFrom={dateFrom} dateTo={dateTo} />
              ))}
            </div>
            
            {/* Pagination */}
            {total > 12 && (
              <div className="flex justify-center gap-2 mt-8">
                <Button
                  variant="outline"
                  disabled={page === 1}
                  onClick={() => {
                    const params = new URLSearchParams(searchParams);
                    params.set('page', String(page - 1));
                    setSearchParams(params);
                  }}
                >
                  {t('common.previous')}
                </Button>
                <span className="flex items-center px-4 text-muted-foreground">
                  {t('common.page')} {page} {t('common.of')} {Math.ceil(total / 12)}
                </span>
                <Button
                  variant="outline"
                  disabled={page * 12 >= total}
                  onClick={() => {
                    const params = new URLSearchParams(searchParams);
                    params.set('page', String(page + 1));
                    setSearchParams(params);
                  }}
                >
                  {t('common.next')}
                </Button>
              </div>
            )}
          </>
        )}
        </>)}
      </div>
    </div>
  );
};

// Equipment Detail Page
export const EquipmentDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [equipment, setEquipment] = useState(null);
  const [seoData, setSeoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [requesting, setRequesting] = useState(false);

  // Request form
  const [startDate, setStartDate] = useState(searchParams.get('from') || '');
  const [endDate, setEndDate] = useState(searchParams.get('to') || '');
  const [message, setMessage] = useState('');

  // Booking modal state — auto-opens when ?action=book is present.
  const [bookingOpen, setBookingOpen] = useState(searchParams.get('action') === 'book');
  const [bookingStep, setBookingStep] = useState(1);
  const [delivery, setDelivery] = useState('pickup'); // 'pickup' | 'delivery'
  const [deliveryAddress, setDeliveryAddress] = useState('');
  // Real-time availability check (calls GET /api/rentals/items/{id}/availability)
  const [availability, setAvailability] = useState({ state: 'idle' });

  // Re-check availability whenever the booking modal is open AND both dates
  // are valid. State machine: idle → checking → available | unavailable.
  useEffect(() => {
    if (!bookingOpen || !startDate || !endDate || startDate > endDate) {
      setAvailability({ state: 'idle' });
      return;
    }
    let cancelled = false;
    setAvailability({ state: 'checking' });
    api
      .get(`/rentals/items/${id}/availability`, {
        start_date: startDate,
        end_date: endDate,
      })
      .then((res) => {
        if (cancelled) return;
        const days = res?.data?.availability || [];
        const blocked = days.filter((d) => !d.is_available);
        if (blocked.length === 0) {
          setAvailability({ state: 'available' });
        } else {
          // Find the earliest free date AFTER the blocked window so we can
          // suggest "next free slot".
          const nextFree = days.find((d) => d.is_available && d.date > blocked[blocked.length - 1].date);
          setAvailability({
            state: 'unavailable',
            nextFree: nextFree?.date,
          });
        }
      })
      .catch(() => {
        if (!cancelled) setAvailability({ state: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [bookingOpen, startDate, endDate, id]);

  useEffect(() => {
    const fetchEquipment = async () => {
      setError(null);
      try {
        const res = await api.getRentalItem(id);
        if (res?.data) {
          setEquipment(res.data);
        } else {
          setError(t('rentals.equipmentNotFound'));
        }
      } catch (err) {
        console.error('Failed to fetch equipment:', err);
        setError(t('rentals.unableToLoadDetails'));
      } finally {
        setLoading(false);
      }
    };
    
    const fetchSeoData = async () => {
      try {
        const res = await api.get(`/public/rentals/${id}`);
        setSeoData(res.data);
      } catch (error) {
        console.log('SEO data not available');
      }
    };
    
    fetchEquipment();
    fetchSeoData();
  }, [id, navigate, t]);

  const handleRequest = async () => {
    if (!user) {
      toast.error(t('rentals.loginToRequest'));
      return;
    }
    
    if (!startDate || !endDate) {
      toast.error(t('rentals.selectDates'));
      return;
    }
    
    setRequesting(true);
    try {
      await api.createRentalRequest(id, {
        start_date: startDate,
        end_date: endDate,
        message
      });
      toast.success(t('rentals.requestSent'));
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('rentals.requestFailed'));
    } finally {
      setRequesting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <RentalsNav />
        <div className="max-w-4xl mx-auto px-4 py-8" data-testid="equipment-detail-loading">
          <Skeleton className="h-12 w-1/2 mb-4" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  // Show error state
  if (error || !equipment) {
    return (
      <div className="min-h-screen bg-background">
        <RentalsNav />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="equipment-detail-error">
          <Button variant="ghost" onClick={() => navigate('/rentals')} className="mb-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('rentals.backToRentals')}
          </Button>
          <Card>
            <CardContent className="py-16 text-center">
              <Package className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-xl font-semibold mb-2">
                {error ? t('common.somethingWentWrong') : t('rentals.equipmentNotFound')}
              </h3>
              <p className="text-muted-foreground mb-4">
                {error || t('rentals.equipmentNotFoundDesc')}
              </p>
              <Link to="/rentals">
                <Button>
                  {t('rentals.browseEquipment')}
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Safe property access for equipment details
  const title = equipment.title || t('common.untitled');
  const description = equipment.description || t('common.noDescription');
  const category = equipment.category;
  const status = equipment.status;
  const city = equipment.city;
  const country = equipment.country;
  const location = [city, country].filter(Boolean).join(', ') || t('common.locationNotSpecified');

  return (
    <div className="min-h-screen bg-background">
      {/* SEO Head */}
      {seoData && (
        <SEOHead
          title={seoData.meta?.title || title}
          description={seoData.meta?.description || description.substring(0, 160)}
          url={seoData.share_url || window.location.href}
          schema={seoData.schema}
          type="product"
        />
      )}
      
      <RentalsNav />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="equipment-detail-page">
        {/* Back button */}
        <Button variant="ghost" onClick={() => navigate('/rentals')} className="mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('rentals.backToRentals')}
        </Button>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="text-4xl mb-2">{categoryIcons[category]}</div>
                  <Badge variant={status === 'available' ? 'success' : 'secondary'}>
                    {status === 'available' ? t('rentals.available') : t('rentals.unavailable')}
                  </Badge>
                </div>
                <CardTitle className="text-2xl">{title}</CardTitle>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPin className="h-4 w-4" />
                  {location}
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2">{t('rentals.description')}</h3>
                  <p className="text-muted-foreground whitespace-pre-line">{description}</p>
                </div>
                
                {(equipment.brand || equipment.model) && (
                  <div>
                    <h3 className="font-semibold mb-2">{t('rentals.details')}</h3>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {equipment.brand && <div><span className="text-muted-foreground">{t('rentals.brand')}:</span> {equipment.brand}</div>}
                      {equipment.model && <div><span className="text-muted-foreground">{t('rentals.model')}:</span> {equipment.model}</div>}
                      {equipment.condition && <div><span className="text-muted-foreground">{t('rentals.condition')}:</span> {equipment.condition}</div>}
                    </div>
                  </div>
                )}
                
                <div className="flex flex-wrap gap-2">
                  {equipment.delivery_available && (
                    <Badge variant="secondary">
                      <Truck className="h-3 w-3 mr-1" /> {t('rentals.deliveryAvailable')}
                    </Badge>
                  )}
                </div>
                
                {/* Share Button */}
                <div className="pt-4 border-t flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{t('rentals.shareRental')}</span>
                  <ShareButton entityType="rental" entityId={equipment.id} title={title} />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Pricing & Request */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('rentals.pricing')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {equipment.price_per_day != null ? (
                  <div className="flex justify-between items-center">
                    <span>{t('rentals.pricePerDay')}</span>
                    <span className="text-xl font-bold text-green-600">€{equipment.price_per_day}</span>
                  </div>
                ) : (
                  <p className="text-muted-foreground">{t('common.contactForPricing')}</p>
                )}
                {equipment.price_per_week != null && (
                  <div className="flex justify-between items-center text-muted-foreground">
                    <span>{t('rentals.pricePerWeek')}</span>
                    <span>€{equipment.price_per_week}</span>
                  </div>
                )}
                {equipment.deposit_amount != null && (
                  <div className="flex justify-between items-center text-muted-foreground border-t pt-3">
                    <span>{t('rentals.depositRequired')}</span>
                    <span>€{equipment.deposit_amount}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {status === 'available' && user && equipment.owner_id !== user?.id && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">{t('rentals.requestRental')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">{t('rentals.startDate')}</label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      data-testid="start-date-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">{t('rentals.endDate')}</label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      min={startDate || new Date().toISOString().split('T')[0]}
                      data-testid="end-date-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">{t('rentals.message')}</label>
                    <Textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder={t('rentals.messagePlaceholder')}
                      rows={3}
                    />
                  </div>
                  <Button 
                    className="w-full" 
                    onClick={handleRequest}
                    disabled={requesting}
                    data-testid="request-rental-btn"
                  >
                    {requesting ? t('common.sending') : t('rentals.sendRequest')}
                  </Button>
                </CardContent>
              </Card>
            )}
            
            {!user && (
              <Card>
                <CardContent className="py-6 text-center">
                  <p className="text-muted-foreground mb-4">{t('rentals.loginToRequest')}</p>
                  <Link to="/login">
                    <Button>{t('auth.login')}</Button>
                  </Link>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Step-by-step booking modal — opens via ?action=book */}
      <Dialog
        open={bookingOpen}
        onOpenChange={(open) => {
          setBookingOpen(open);
          if (!open) {
            const params = new URLSearchParams(searchParams);
            params.delete('action');
            setSearchParams(params, { replace: true });
            setBookingStep(1);
          }
        }}
      >
        <DialogContent className="max-w-md" data-testid="booking-modal">
          <DialogHeader>
            <DialogTitle>
              {t('rentals.bookingTitle')} · {equipment?.title}
            </DialogTitle>
          </DialogHeader>

          {/* Stepper indicator */}
          <div className="flex items-center justify-between text-xs font-medium text-slate-600 mb-2" data-testid="booking-stepper">
            {[1, 2, 3, 4].map((n) => (
              <div key={n} className="flex-1 flex items-center">
                <span
                  className={`h-6 w-6 rounded-full flex items-center justify-center text-[11px] font-bold ${
                    n <= bookingStep ? 'bg-orange-500 text-white' : 'bg-slate-200 text-slate-500'
                  }`}
                  data-testid={`booking-step-indicator-${n}`}
                >
                  {n}
                </span>
                {n < 4 && <div className={`h-0.5 flex-1 mx-1 ${n < bookingStep ? 'bg-orange-500' : 'bg-slate-200'}`} />}
              </div>
            ))}
          </div>

          {bookingStep === 1 && (
            <div className="space-y-3" data-testid="booking-step-1">
              <div className="text-sm font-medium">{t('rentals.bookingStep1Title')}</div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-slate-600">{t('rentals.dateFromLabel')}</label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    min={new Date().toISOString().slice(0, 10)}
                    data-testid="booking-start-date"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-600">{t('rentals.dateToLabel')}</label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate || new Date().toISOString().slice(0, 10)}
                    data-testid="booking-end-date"
                  />
                </div>
              </div>
              {startDate && endDate && startDate > endDate && (
                <div className="text-xs text-red-600" data-testid="booking-date-error">
                  {t('rentals.dateRangeError')}
                </div>
              )}
              {/* Real-time availability indicator */}
              {availability.state === 'checking' && (
                <div className="text-xs text-slate-500" data-testid="booking-availability-checking">
                  {t('common.loading')}…
                </div>
              )}
              {availability.state === 'available' && (
                <div className="flex items-center gap-1.5 text-xs font-medium text-emerald-700" data-testid="booking-availability-ok">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  {t('rentals.available')}
                </div>
              )}
              {availability.state === 'unavailable' && (
                <div className="flex items-center gap-1.5 text-xs font-medium text-red-700" data-testid="booking-availability-blocked">
                  <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                  {availability.nextFree
                    ? `${t('rentals.bookedNextFree')} ${availability.nextFree}`
                    : t('rentals.unavailable')}
                </div>
              )}
              {availability.state === 'error' && (
                <div className="text-xs text-amber-700" data-testid="booking-availability-error">
                  {t('rentals.availabilityCheckError')}
                </div>
              )}
            </div>
          )}

          {bookingStep === 2 && (
            <div className="space-y-3" data-testid="booking-step-2">
              <div className="text-sm font-medium">{t('rentals.bookingStep2Title')}</div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setDelivery('pickup')}
                  data-testid="booking-delivery-pickup"
                  className={`flex-1 rounded-lg border px-3 py-2 text-sm transition ${
                    delivery === 'pickup' ? 'border-orange-500 bg-orange-50 text-orange-800' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {t('rentals.bookingPickup')}
                </button>
                <button
                  type="button"
                  onClick={() => setDelivery('delivery')}
                  data-testid="booking-delivery-shipped"
                  className={`flex-1 rounded-lg border px-3 py-2 text-sm transition ${
                    delivery === 'delivery' ? 'border-orange-500 bg-orange-50 text-orange-800' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {t('rentals.bookingDelivery')}
                </button>
              </div>
              {delivery === 'delivery' && (
                <Input
                  placeholder={t('rentals.bookingAddressPlaceholder')}
                  value={deliveryAddress}
                  onChange={(e) => setDeliveryAddress(e.target.value)}
                  data-testid="booking-address"
                />
              )}
            </div>
          )}

          {bookingStep === 3 && (
            <div className="space-y-3" data-testid="booking-step-3">
              <div className="text-sm font-medium">{t('rentals.bookingStep3Title')}</div>
              <Textarea
                placeholder={t('rentals.messagePlaceholder')}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                data-testid="booking-message"
              />
            </div>
          )}

          {bookingStep === 4 && (
            <div className="space-y-2 text-sm" data-testid="booking-step-4">
              <div className="font-medium">{t('rentals.bookingStep4Title')}</div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-1">
                <div><span className="text-slate-500">{t('rentals.dateFromLabel')}:</span> <span data-testid="booking-summary-from">{startDate || '—'}</span></div>
                <div><span className="text-slate-500">{t('rentals.dateToLabel')}:</span> <span data-testid="booking-summary-to">{endDate || '—'}</span></div>
                <div><span className="text-slate-500">{t('rentals.bookingDeliveryMode')}:</span> <span data-testid="booking-summary-delivery">{delivery === 'pickup' ? t('rentals.bookingPickup') : t('rentals.bookingDelivery')}</span></div>
                {delivery === 'delivery' && deliveryAddress && (
                  <div><span className="text-slate-500">{t('rentals.bookingAddressLabel')}:</span> {deliveryAddress}</div>
                )}
                {equipment?.price_per_day && startDate && endDate && (
                  <div className="pt-1 border-t border-slate-200 mt-1">
                    <span className="text-slate-500">{t('rentals.bookingEstimate')}:</span>{' '}
                    <span className="font-semibold" data-testid="booking-summary-total">
                      €{(equipment.price_per_day * Math.max(1, Math.ceil((new Date(endDate) - new Date(startDate)) / 86400000))).toFixed(0)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="flex flex-row gap-2 sm:justify-between">
            <Button
              variant="outline"
              size="sm"
              disabled={bookingStep === 1}
              onClick={() => setBookingStep((s) => Math.max(1, s - 1))}
              data-testid="booking-prev"
            >
              {t('common.previous')}
            </Button>
            {bookingStep < 4 ? (
              <Button
                size="sm"
                className="bg-orange-500 hover:bg-orange-600"
                onClick={() => setBookingStep((s) => Math.min(4, s + 1))}
                disabled={
                  bookingStep === 1 &&
                  (!startDate ||
                    !endDate ||
                    startDate > endDate ||
                    availability.state === 'checking' ||
                    availability.state === 'unavailable')
                }
                data-testid="booking-next"
              >
                {t('common.next')}
              </Button>
            ) : (
              <Button
                size="sm"
                className="bg-orange-500 hover:bg-orange-600"
                disabled={requesting}
                onClick={handleRequest}
                data-testid="booking-confirm"
              >
                {requesting ? t('common.loading') : t('rentals.bookingConfirm')}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Create Equipment Page
export const CreateEquipment = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    custom_category: '',
    brand: '',
    model: '',
    condition: 'good',
    price_per_day: '',
    price_per_week: '',
    deposit_amount: '',
    city: '',
    country: 'FI',
    delivery_available: false,
    specs: [],
  });

  // Per-category spec presets. Each preset is an array of rows the form
  // pre-fills when the category changes. User can still add arbitrary rows
  // via "+ Lisa spec".
  const SPEC_PRESETS = {
    construction: [
      { key: 'power', unit: 'kW' },
      { key: 'voltage', unit: 'V' },
      { key: 'phase', unit: '' }, // 1 or 3
      { key: 'fuel', unit: '' }, // diesel / petrol
      { key: 'noise', unit: 'dB' },
      { key: 'weight', unit: 'kg' },
    ],
    welding: [
      { key: 'amps', unit: 'A' },
      { key: 'process', unit: '' }, // MIG / TIG / MMA
      { key: 'voltage', unit: 'V' },
      { key: 'wire_diameter', unit: 'mm' },
      { key: 'gas', unit: '' }, // yes / no
    ],
    power_tools: [
      { key: 'wattage', unit: 'W' },
      { key: 'voltage', unit: 'V' },
      { key: 'battery', unit: 'Ah' },
      { key: 'rpm', unit: 'RPM' },
      { key: 'sds', unit: '' },
    ],
    vehicles: [
      { key: 'body_type', unit: '' }, // van / pickup / trailer / lorry
      { key: 'payload', unit: 'kg' },
      { key: 'cargo_volume', unit: 'm³' },
      { key: 'tow_hitch', unit: '' }, // yes / no
      { key: 'drive', unit: '' }, // FWD / RWD / 4x4
      { key: 'gearbox', unit: '' }, // manual / auto
      { key: 'fuel', unit: '' },
    ],
    lifting: [
      { key: 'capacity', unit: 'kg' },
      { key: 'reach', unit: 'm' },
    ],
  };

  // Add ALL preset rows for the selected category, skipping any keys the
  // user has already entered. New rows have empty values so the user can
  // fill them in.
  useEffect(() => {
    const presetRows = SPEC_PRESETS[formData.category];
    if (!Array.isArray(presetRows) || presetRows.length === 0) return;
    setFormData((prev) => {
      const existingKeys = new Set(prev.specs.map((s) => s.key));
      const additions = presetRows
        .filter((p) => !existingKeys.has(p.key))
        .map((p) => ({ key: p.key, value: '', unit: p.unit }));
      if (additions.length === 0) return prev;
      return { ...prev, specs: [...prev.specs, ...additions] };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.category]);

  const updateSpec = (idx, patch) => {
    setFormData((prev) => {
      const next = [...prev.specs];
      next[idx] = { ...next[idx], ...patch };
      return { ...prev, specs: next };
    });
  };
  const removeSpec = (idx) => {
    setFormData((prev) => ({ ...prev, specs: prev.specs.filter((_, i) => i !== idx) }));
  };
  const addSpecRow = () => {
    setFormData((prev) => ({ ...prev, specs: [...prev.specs, { key: '', value: '', unit: '' }] }));
  };

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    const fetchCategories = async () => {
      try {
        const res = await api.getRentalCategories();
        setCategories(res.data.categories || []);
      } catch (error) {
        console.error('Failed to fetch categories');
      }
    };
    fetchCategories();
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description || !formData.category || !formData.price_per_day) {
      toast.error(t('rentals.createEquipment.fillRequiredFields'));
      return;
    }
    
    // Validate custom category if "other" is selected
    if (formData.category === 'other' && !formData.custom_category?.trim()) {
      toast.error(t('rentals.createEquipment.customCategoryRequired'));
      return;
    }
    
    setLoading(true);
    try {
      const data = {
        ...formData,
        price_per_day: parseFloat(formData.price_per_day),
        price_per_week: formData.price_per_week ? parseFloat(formData.price_per_week) : null,
        deposit_amount: formData.deposit_amount ? parseFloat(formData.deposit_amount) : null,
        custom_category: formData.category === 'other' ? formData.custom_category?.trim() : null,
        specs: (formData.specs || []).filter((s) => s.key && s.value !== ''),
      };
      
      await api.createRentalItem(data);
      toast.success(t('rentals.createEquipment.success'));
      navigate('/rentals');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('rentals.createEquipment.failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <RentalsNav />
      
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="create-equipment-page">
        <Button variant="ghost" onClick={() => navigate('/rentals')} className="mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('rentals.backToRentals')}
        </Button>

        <Card>
          <CardHeader>
            <CardTitle>{t('rentals.createEquipment.title')}</CardTitle>
            <CardDescription>{t('rentals.createEquipment.subtitle')}</CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="text-sm font-medium">{t('rentals.createEquipment.titleLabel')} *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder={t('rentals.createEquipment.titlePlaceholder')}
                  data-testid="equipment-title-input"
                />
              </div>

              <div>
                <label className="text-sm font-medium">{t('rentals.createEquipment.descriptionLabel')} *</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder={t('rentals.createEquipment.descriptionPlaceholder')}
                  rows={4}
                  data-testid="equipment-description-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">{t('rentals.createEquipment.categoryLabel')} *</label>
                  <CategoryCombobox
                    categories={categories}
                    value={formData.category}
                    onValueChange={(v) => setFormData(prev => ({...prev, category: v}))}
                    customValue={formData.custom_category}
                    onCustomValueChange={(v) => setFormData(prev => ({...prev, custom_category: v}))}
                    placeholder={t('rentals.createEquipment.categoryPlaceholder')}
                    data-testid="equipment-category-select"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">{t('rentals.createEquipment.conditionLabel')}</label>
                  <Select 
                    value={formData.condition} 
                    onValueChange={(v) => setFormData({...formData, condition: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">{t('rentals.conditions.new')}</SelectItem>
                      <SelectItem value="excellent">{t('rentals.conditions.excellent')}</SelectItem>
                      <SelectItem value="good">{t('rentals.conditions.good')}</SelectItem>
                      <SelectItem value="fair">{t('rentals.conditions.fair')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">{t('rentals.createEquipment.brandLabel')}</label>
                  <Input
                    value={formData.brand}
                    onChange={(e) => setFormData({...formData, brand: e.target.value})}
                    placeholder={t('rentals.createEquipment.brandPlaceholder')}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">{t('rentals.createEquipment.modelLabel')}</label>
                  <Input
                    value={formData.model}
                    onChange={(e) => setFormData({...formData, model: e.target.value})}
                    placeholder={t('rentals.createEquipment.modelPlaceholder')}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium">{t('rentals.createEquipment.pricePerDayLabel')} *</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.price_per_day}
                    onChange={(e) => setFormData({...formData, price_per_day: e.target.value})}
                    placeholder={t('rentals.createEquipment.pricePerDayPlaceholder')}
                    data-testid="equipment-price-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">{t('rentals.createEquipment.pricePerWeekLabel')}</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.price_per_week}
                    onChange={(e) => setFormData({...formData, price_per_week: e.target.value})}
                    placeholder={t('rentals.createEquipment.pricePerWeekPlaceholder')}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">{t('rentals.createEquipment.depositLabel')}</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.deposit_amount}
                    onChange={(e) => setFormData({...formData, deposit_amount: e.target.value})}
                    placeholder={t('rentals.createEquipment.depositPlaceholder')}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">{t('rentals.createEquipment.cityLabel')} *</label>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    placeholder={t('rentals.createEquipment.cityPlaceholder')}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">{t('rentals.createEquipment.countryLabel')}</label>
                  <Select 
                    value={formData.country} 
                    onValueChange={(v) => setFormData({...formData, country: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="FI">{t('countries.FI')}</SelectItem>
                      <SelectItem value="EE">{t('countries.EE')}</SelectItem>
                      <SelectItem value="SE">{t('countries.SE')}</SelectItem>
                      <SelectItem value="NO">{t('countries.NO')}</SelectItem>
                      <SelectItem value="LT">{t('countries.LT')}</SelectItem>
                      <SelectItem value="LV">{t('countries.LV')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Specs editor — dynamic key/value/unit rows. Auto-injects
                  category preset (kW for construction, A for welding, W for
                  power_tools) and lets user add arbitrary rows. */}
              <div className="space-y-2" data-testid="specs-editor">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">{t('rentals.createEquipment.specsLabel')}</label>
                  <Button type="button" variant="outline" size="sm" onClick={addSpecRow} data-testid="add-spec-row">
                    + {t('rentals.createEquipment.addSpec')}
                  </Button>
                </div>
                {formData.specs.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    {t('rentals.createEquipment.specsHint')}
                  </p>
                ) : (
                  formData.specs.map((s, i) => (
                    <div className="grid grid-cols-12 gap-2 items-center" key={i} data-testid={`spec-row-${i}`}>
                      <Input
                        className="col-span-5"
                        placeholder={t('rentals.createEquipment.specKeyPh')}
                        value={s.key}
                        onChange={(e) => updateSpec(i, { key: e.target.value })}
                      />
                      <Input
                        className="col-span-4"
                        placeholder={t('rentals.createEquipment.specValuePh')}
                        value={s.value}
                        onChange={(e) => updateSpec(i, { value: e.target.value })}
                      />
                      <Input
                        className="col-span-2"
                        placeholder={t('rentals.createEquipment.specUnitPh')}
                        value={s.unit || ''}
                        onChange={(e) => updateSpec(i, { unit: e.target.value })}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="col-span-1 text-red-600 hover:text-red-700"
                        onClick={() => removeSpec(i)}
                        data-testid={`remove-spec-${i}`}
                      >
                        ×
                      </Button>
                    </div>
                  ))
                )}
              </div>

              <Button type="submit" className="w-full" disabled={loading} data-testid="submit-equipment-btn">
                {loading ? t('common.creating') : t('rentals.createEquipment.submitButton')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EquipmentRentals;
